// frontend/src/pages/PostAd.jsx
import React, { useMemo, useState } from "react";
import p2pAxios from "../p2pAxios";
import { toast } from "react-toastify";

const EXPIRE_OPTIONS = [15, 30, 45, 60, 90, 180];
const DEFAULT_EXPIRE = 45;

const COIN_OPTIONS = ["USDT", "BTC", "ETH", "USDC"]; // keep in sync with backend
const PAYMENT_OPTIONS = ["Bank Transfer", "UPI"];

const rupee = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const clampStrLen = (s, max) => String(s || "").slice(0, max);

export default function PostAd() {
  const [type, setType] = useState("buy"); // 'buy' | 'sell'
  const [coin, setCoin] = useState("USDT");
  const [price, setPrice] = useState("");
  const [minLimit, setMinLimit] = useState("");
  const [maxLimit, setMaxLimit] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [terms, setTerms] = useState("");
  const [autoReply, setAutoReply] = useState("");
  const [expireMinutes, setExpireMinutes] = useState(DEFAULT_EXPIRE);
  const [loading, setLoading] = useState(false);

  const parsed = useMemo(() => {
    const p = parseFloat(String(price || "").trim());
    const min = parseFloat(String(minLimit || "").trim());
    const max = parseFloat(String(maxLimit || "").trim());
    return {
      price: Number.isFinite(p) ? p : NaN,
      min: Number.isFinite(min) ? min : NaN,
      max: Number.isFinite(max) ? max : NaN,
    };
  }, [price, minLimit, maxLimit]);

  const errors = useMemo(() => {
    const e = {};
    if (!["buy", "sell"].includes(type)) e.type = "Choose Buy or Sell.";
    if (!COIN_OPTIONS.includes(coin)) e.coin = "Choose a valid coin.";
    if (!Number.isFinite(parsed.price) || parsed.price <= 0)
      e.price = "Enter a valid price.";
    if (!Number.isFinite(parsed.min) || parsed.min <= 0)
      e.minLimit = "Enter a valid minimum limit.";
    if (!Number.isFinite(parsed.max) || parsed.max <= 0)
      e.maxLimit = "Enter a valid maximum limit.";
    if (
      Number.isFinite(parsed.min) &&
      Number.isFinite(parsed.max) &&
      parsed.min > parsed.max
    )
      e.limitRange = "Minimum cannot be greater than maximum.";
    if (!paymentMethods.length) e.paymentMethods = "Select at least one method.";
    if (!String(terms).trim()) e.terms = "Terms are required.";
    if (!EXPIRE_OPTIONS.includes(expireMinutes))
      e.expireMinutes = "Choose a valid expiry.";
    return e;
  }, [type, coin, parsed, paymentMethods, terms, expireMinutes]);

  const valid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const toggleMethod = (method) => {
    setPaymentMethods((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method]
    );
  };

  const resetForm = () => {
    setType("buy");
    setCoin("USDT");
    setPrice("");
    setMinLimit("");
    setMaxLimit("");
    setPaymentMethods([]);
    setTerms("");
    setAutoReply("");
    setExpireMinutes(DEFAULT_EXPIRE);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await p2pAxios.post("/ads/create", {
        type,
        coin,
        price: parsed.price,
        minLimit: parsed.min,
        maxLimit: parsed.max,
        paymentMethods,
        terms: terms.trim(),
        autoReply: autoReply?.trim() || undefined,
        expireMinutes,
      });

      if (res.data?.success) {
        toast.success("Ad posted successfully!");
        resetForm();
      } else {
        toast.error(res.data?.message || "Failed to post ad.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to post ad.");
    } finally {
      setLoading(false);
    }
  };

  const termsLeft = 800 - (terms?.length || 0);
  const autoLeft = 300 - (autoReply?.length || 0);

  const showPreview =
    Number.isFinite(parsed.price) &&
    Number.isFinite(parsed.min) &&
    Number.isFinite(parsed.max) &&
    parsed.price > 0 &&
    parsed.min > 0 &&
    parsed.max > 0 &&
    parsed.min <= parsed.max;

  const typePill =
    type === "buy"
      ? "bg-emerald-600 text-white border-emerald-700"
      : "bg-rose-600 text-white border-rose-700";

  return (
    <div className="w-full">
      {/* Scroll container + extra bottom padding for sticky action bar (and mobile bottom tabs if any) */}
      <div className="max-w-5xl mx-auto md:mt-8 mt-3 px-2 sm:px-3 md:px-0 pb-28 md:pb-0">
        <div className="bg-white dark:bg-slate-900 md:rounded-2xl rounded-xl md:shadow-lg shadow border border-slate-200/70 dark:border-slate-800 overflow-hidden">
          {/* Header */}
          <div className="px-4 md:px-6 pt-4 md:pt-6 pb-3 md:pb-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-[15px] md:text-2xl font-bold text-slate-900 dark:text-white leading-tight">
                  Post P2P Ad
                </h2>
                <p className="text-[12px] md:text-base text-slate-600 dark:text-slate-300 mt-1">
                  Create a professional ad with price, limits, payment methods and terms.
                </p>
              </div>

              {/* Compact “Type” badge (visual cue) */}
              <div
                className={`shrink-0 inline-flex items-center rounded-full border px-3 py-1 text-[11px] md:text-sm font-semibold ${typePill}`}
              >
                {type === "buy" ? "BUY AD" : "SELL AD"}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
            {/* Section: Basics */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/30 p-3 md:p-4">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[12px] md:text-sm font-semibold text-slate-900 dark:text-white">
                  Basics
                </p>
                <p className="text-[11px] md:text-xs text-slate-500 dark:text-slate-400">
                  Required
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {/* Ad Type */}
                <div>
                  <label className="block text-[12px] md:text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                    Ad Type
                  </label>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setType("buy")}
                      className={`h-9 md:h-10 rounded-lg border text-[12px] md:text-sm font-semibold transition ${
                        type === "buy"
                          ? "bg-emerald-600 border-emerald-700 text-white"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      Buy
                    </button>
                    <button
                      type="button"
                      onClick={() => setType("sell")}
                      className={`h-9 md:h-10 rounded-lg border text-[12px] md:text-sm font-semibold transition ${
                        type === "sell"
                          ? "bg-rose-600 border-rose-700 text-white"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      Sell
                    </button>
                  </div>

                  {!!errors.type && (
                    <p className="mt-1 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                      {errors.type}
                    </p>
                  )}
                </div>

                {/* Coin */}
                <div>
                  <label className="block text-[12px] md:text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                    Coin
                  </label>

                  <div className="flex flex-wrap gap-1.5 md:gap-2">
                    {COIN_OPTIONS.map((c) => {
                      const active = coin === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCoin(c)}
                          className={`h-9 md:h-10 px-3 rounded-lg border text-[12px] md:text-sm font-semibold transition ${
                            active
                              ? "bg-blue-600 border-blue-700 text-white"
                              : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                          }`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>

                  {!!errors.coin && (
                    <p className="mt-1 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                      {errors.coin}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section: Pricing */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 md:p-4">
              <p className="text-[12px] md:text-sm font-semibold text-slate-900 dark:text-white mb-2.5">
                Pricing & Limits
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                {/* Price */}
                <div className="md:col-span-1">
                  <label className="block text-[12px] md:text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                    Price per {coin} (₹)
                  </label>

                  <div
                    className={`flex items-stretch rounded-lg border ${
                      errors.price ? "border-rose-300 dark:border-rose-700" : "border-slate-200 dark:border-slate-700"
                    } overflow-hidden bg-white dark:bg-slate-900`}
                  >
                    <span className="inline-flex items-center px-2.5 text-[12px] md:text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700">
                      ₹
                    </span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="e.g. 85.50"
                      className="w-full h-9 md:h-10 px-2.5 text-[13px] md:text-sm bg-transparent text-slate-900 dark:text-white focus:outline-none"
                    />
                  </div>

                  {Number.isFinite(parsed.price) && parsed.price > 0 && (
                    <p className="mt-1 text-[11px] md:text-xs text-slate-500 dark:text-slate-400">
                      1 {coin} = <span className="font-semibold">{rupee(parsed.price)}</span>
                    </p>
                  )}

                  {!!errors.price && (
                    <p className="mt-1 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                      {errors.price}
                    </p>
                  )}
                </div>

                {/* Min */}
                <div className="md:col-span-1">
                  <label className="block text-[12px] md:text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                    Minimum Limit (₹)
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    value={minLimit}
                    onChange={(e) => setMinLimit(e.target.value)}
                    placeholder="e.g. 1000"
                    className={`w-full h-9 md:h-10 rounded-lg border px-2.5 text-[13px] md:text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${
                      errors.minLimit || errors.limitRange
                        ? "border-rose-300 dark:border-rose-700 focus:ring-rose-200 dark:focus:ring-rose-600"
                        : "border-slate-200 dark:border-slate-700 focus:ring-blue-200 dark:focus:ring-blue-600"
                    }`}
                  />
                  {Number.isFinite(parsed.min) && parsed.min > 0 && (
                    <p className="mt-1 text-[11px] md:text-xs text-slate-500 dark:text-slate-400">
                      Min: <span className="font-semibold">{rupee(parsed.min)}</span>
                    </p>
                  )}
                  {!!errors.minLimit && (
                    <p className="mt-1 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                      {errors.minLimit}
                    </p>
                  )}
                </div>

                {/* Max */}
                <div className="md:col-span-1">
                  <label className="block text-[12px] md:text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                    Maximum Limit (₹)
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    value={maxLimit}
                    onChange={(e) => setMaxLimit(e.target.value)}
                    placeholder="e.g. 50000"
                    className={`w-full h-9 md:h-10 rounded-lg border px-2.5 text-[13px] md:text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${
                      errors.maxLimit || errors.limitRange
                        ? "border-rose-300 dark:border-rose-700 focus:ring-rose-200 dark:focus:ring-rose-600"
                        : "border-slate-200 dark:border-slate-700 focus:ring-blue-200 dark:focus:ring-blue-600"
                    }`}
                  />
                  {Number.isFinite(parsed.max) && parsed.max > 0 && (
                    <p className="mt-1 text-[11px] md:text-xs text-slate-500 dark:text-slate-400">
                      Max: <span className="font-semibold">{rupee(parsed.max)}</span>
                    </p>
                  )}
                  {!!errors.maxLimit && (
                    <p className="mt-1 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                      {errors.maxLimit}
                    </p>
                  )}
                </div>
              </div>

              {!!errors.limitRange && (
                <p className="mt-2 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                  {errors.limitRange}
                </p>
              )}
            </div>

            {/* Section: Payment */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 md:p-4">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[12px] md:text-sm font-semibold text-slate-900 dark:text-white">
                  Payment Methods
                </p>
                <p className="text-[11px] md:text-xs text-slate-500 dark:text-slate-400">
                  Select one or more
                </p>
              </div>

              <div className="flex flex-wrap gap-1.5 md:gap-2">
                {PAYMENT_OPTIONS.map((m) => {
                  const active = paymentMethods.includes(m);
                  return (
                    <button
                      type="button"
                      key={m}
                      onClick={() => toggleMethod(m)}
                      className={`h-9 md:h-10 px-3 rounded-lg border text-[12px] md:text-sm font-semibold transition ${
                        active
                          ? "bg-blue-600 border-blue-700 text-white"
                          : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-700"
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>

              {!!errors.paymentMethods && (
                <p className="mt-2 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                  {errors.paymentMethods}
                </p>
              )}
            </div>

            {/* Section: Terms */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 md:p-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[12px] md:text-sm font-semibold text-slate-900 dark:text-white">
                  Terms &amp; Conditions <span className="text-rose-500">*</span>
                </label>
                <span className="text-[11px] md:text-xs text-slate-500 dark:text-slate-400">
                  {termsLeft} left
                </span>
              </div>

              <textarea
                value={terms}
                onChange={(e) => setTerms(clampStrLen(e.target.value, 800))}
                rows={4}
                className={`w-full rounded-lg border p-2.5 md:p-3 text-[13px] md:text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${
                  errors.terms
                    ? "border-rose-300 dark:border-rose-700 focus:ring-rose-200 dark:focus:ring-rose-600"
                    : "border-slate-200 dark:border-slate-700 focus:ring-blue-200 dark:focus:ring-blue-600"
                }`}
                placeholder="Payment rules, proof requirements, communication instructions, etc."
              />

              {!!errors.terms && (
                <p className="mt-1 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                  {errors.terms}
                </p>
              )}
            </div>

            {/* Section: Auto Reply */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 md:p-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[12px] md:text-sm font-semibold text-slate-900 dark:text-white">
                  Auto-Reply (optional)
                </label>
                <span className="text-[11px] md:text-xs text-slate-500 dark:text-slate-400">
                  {autoLeft} left
                </span>
              </div>

              <textarea
                value={autoReply}
                onChange={(e) => setAutoReply(clampStrLen(e.target.value, 300))}
                rows={3}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 p-2.5 md:p-3 text-[13px] md:text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-600"
                placeholder="Sent automatically when a trade starts (optional)."
              />
            </div>

            {/* Section: Expiry */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/30 p-3 md:p-4">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[12px] md:text-sm font-semibold text-slate-900 dark:text-white">
                  Trade Expiry
                </p>
                <p className="text-[11px] md:text-xs text-slate-500 dark:text-slate-400">
                  Choose timeout
                </p>
              </div>

              {/* Horizontal scroll on mobile for compact look */}
              <div className="-mx-1 px-1 overflow-x-auto">
                <div className="flex gap-2 min-w-max">
                  {EXPIRE_OPTIONS.map((opt) => {
                    const active = expireMinutes === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setExpireMinutes(opt)}
                        disabled={loading}
                        className={`h-9 md:h-10 px-3 rounded-lg border text-[12px] md:text-sm font-semibold transition ${
                          active
                            ? "bg-blue-600 border-blue-700 text-white"
                            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                        }`}
                      >
                        {opt}m
                      </button>
                    );
                  })}
                </div>
              </div>

              <p className="mt-2 text-[11px] md:text-xs text-slate-600 dark:text-slate-300">
                If not marked as <span className="font-semibold">Paid</span> within{" "}
                <span className="font-semibold">{expireMinutes} minutes</span>, trade will expire automatically.
              </p>

              {!!errors.expireMinutes && (
                <p className="mt-1 text-[11px] md:text-xs text-rose-600 dark:text-rose-400">
                  {errors.expireMinutes}
                </p>
              )}
            </div>

            {/* Preview */}
            {showPreview && (
              <div className="rounded-xl border border-blue-200 dark:border-slate-700 bg-blue-50 dark:bg-slate-800/60 p-3 md:p-4">
                <p className="text-[12px] md:text-sm font-semibold text-slate-900 dark:text-white mb-1">
                  Preview
                </p>

                <div className="flex flex-wrap items-center gap-2 text-[12px] md:text-sm text-slate-700 dark:text-slate-200">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold ${typePill}`}>
                    {type.toUpperCase()}
                  </span>
                  <span className="font-semibold">{coin}</span>
                  <span className="text-slate-400">•</span>
                  <span>
                    Price: <span className="font-semibold">{rupee(parsed.price)}</span>
                  </span>
                  <span className="text-slate-400">•</span>
                  <span>
                    Limits:{" "}
                    <span className="font-semibold">
                      {rupee(parsed.min)} – {rupee(parsed.max)}
                    </span>
                  </span>
                </div>

                {!!paymentMethods.length && (
                  <p className="mt-2 text-[12px] md:text-sm text-slate-700 dark:text-slate-200">
                    Payment: <span className="font-semibold">{paymentMethods.join(", ")}</span>
                  </p>
                )}

                <p className="mt-1 text-[11px] md:text-xs text-blue-800 dark:text-blue-300">
                  Expiry: {expireMinutes} minutes
                </p>
              </div>
            )}

            {/* Desktop actions (normal) */}
            <div className="hidden md:flex items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={!valid || loading}
                className={`h-10 px-5 rounded-lg font-semibold text-white transition ${
                  !valid || loading ? "bg-slate-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {loading ? "Posting…" : "Post Ad"}
              </button>

              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="h-10 px-5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
              >
                Reset
              </button>

              {!valid && (
                <p className="text-[12px] text-rose-600 dark:text-rose-400 ml-auto">
                  Please fix the highlighted fields to post.
                </p>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Mobile sticky action bar */}
      <div className="md:hidden fixed left-0 right-0 bottom-0 z-40 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 px-3 pb-[calc(env(safe-area-inset-bottom,0px)+10px)] pt-2">
        {!valid ? (
          <div className="mb-1 text-[11px] text-rose-600 dark:text-rose-400">
            Fix the highlighted fields to post.
          </div>
        ) : (
          <div className="mb-1 text-[11px] text-slate-500 dark:text-slate-400">
            Ready to post your ad.
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!valid || loading}
            className={`flex-1 h-11 rounded-xl font-semibold text-white transition ${
              !valid || loading ? "bg-slate-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Posting…" : "Post Ad"}
          </button>

          <button
            type="button"
            onClick={resetForm}
            disabled={loading}
            className="h-11 px-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
